import { useState, useRef, useEffect } from "react";
import { getEmailsDashboard } from "../../api/emailsApi";
import PageLoader from "../../components/pageLoader";
import EmailsLogsSection from './EmailsLogs';

// ── Helpers ──
const fmt = (n) => n?.toLocaleString("en-GB") ?? "—";

const attemptBadge = (val) => {
  if (!val) return <span className="text-slate-600 text-xs">—</span>;
  const style =
    val === "delivered"
      ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
      : val === "bounced"
        ? "bg-red-500/10 border-red-500/20 text-red-400"
        : val === "sent"
          ? "bg-blue-500/10 border-blue-500/20 text-blue-400"
          : "bg-slate-500/10 border-slate-500/20 text-slate-400";
  return (
    <span
      className={`px-2 py-0.5 rounded-md text-xs font-medium border ${style}`}
    >
      {val}
    </span>
  );
};

// ── Format bucket label from ISO timestamp ──
const formatBucketLabel = (at, range) => {
  if (!at) return "";
  const date = new Date(at);
  if (range === "1d") {
    return date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }
  return date.toLocaleDateString("en-GB", { month: "short", day: "numeric" });
};

// ── Delivery Trend Chart ──
const DeliveryTrendChart = ({ trend, range, onRangeChange, loading }) => {
  const [tooltip, setTooltip] = useState(null);
  const svgRef = useRef(null);

  const SERIES = [
    { key: "sent", label: "Sent", color: "#60a5fa" },
    { key: "delivered", label: "Delivered", color: "#34d399" },
    { key: "bounced", label: "Bounced", color: "#f87171" },
    { key: "auto_resent", label: "Auto Resent", color: "#facc15" },
    { key: "needs_action", label: "Needs Action", color: "#f472b6" },
  ];

  const rawBuckets = trend?.buckets || [];

  // Normalize buckets: map API keys and add display label
  const buckets = rawBuckets.map((b) => ({
    ...b,
    label: formatBucketLabel(b.at, range),
    // API may use "needs_action" already; keep as-is
  }));

  const W = 560,
    H = 180;
  const padL = 36,
    padR = 12,
    padT = 12,
    padB = 28;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  const maxVal = Math.max(
    ...buckets.flatMap((b) => SERIES.map((s) => b[s.key] || 0)),
    1,
  );

  const xPos = (i) => padL + (i / (buckets.length - 1 || 1)) * chartW;
  const yPos = (val) => padT + chartH - (val / maxVal) * chartH;

  const gridLines = [0, 0.25, 0.5, 0.75, 1].map((v) => ({
    y: yPos(v * maxVal),
    label: Math.round(v * maxVal),
  }));

  const makePath = (key) =>
    buckets.reduce((acc, b, i) => {
      const x = xPos(i),
        y = yPos(b[key] || 0);
      if (i === 0) return `M ${x} ${y}`;
      const prev = buckets[i - 1];
      const cpX = (xPos(i - 1) + x) / 2;
      return `${acc} C ${cpX} ${yPos(prev[key] || 0)}, ${cpX} ${y}, ${x} ${y}`;
    }, "");

  const handleMouseMove = (e) => {
    if (!svgRef.current || !buckets.length) return;
    const rect = svgRef.current.getBoundingClientRect();
    const mouseX = ((e.clientX - rect.left) / rect.width) * W;
    let idx = 0,
      dist = Infinity;
    buckets.forEach((_, i) => {
      const d = Math.abs(xPos(i) - mouseX);
      if (d < dist) {
        dist = d;
        idx = i;
      }
    });
    setTooltip({
      svgX: xPos(idx),
      clientX: e.clientX - rect.left,
      bucket: buckets[idx],
    });
  };

  // Determine which x-labels to show to avoid crowding
  const labelStep =
    buckets.length > 14
      ? Math.ceil(buckets.length / 7)
      : buckets.length > 6
        ? 2
        : 1;

  return (
    <div>
      {/* Range toggle */}
      <div className="flex items-center gap-1 mb-3">
        {[
          { key: "1d", label: "24h" },
          { key: "7d", label: "7d" },
          { key: "30d", label: "30d" },
        ].map((r) => (
          <button
            key={r.key}
            onClick={() => onRangeChange(r.key)}
            className={`px-3 py-1 rounded-md text-xs font-medium transition ${
              range === r.key
                ? "bg-pink-600 text-white"
                : "text-slate-400 hover:text-white hover:bg-slate-800 border border-white/10"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center gap-3">
          <div className="w-5 h-5 border-2 border-white/10 border-t-pink-500 rounded-full animate-spin" />
          <span className="text-sm text-slate-500">Loading chart data...</span>
        </div>
      ) : (
        <div className="relative select-none">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${W} ${H}`}
            className="w-full cursor-crosshair"
            style={{ height: 200 }}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setTooltip(null)}
          >
            {/* Grid */}
            {gridLines.map((g, i) => (
              <g key={i}>
                <line
                  x1={padL}
                  y1={g.y}
                  x2={W - padR}
                  y2={g.y}
                  stroke="rgba(255,255,255,0.05)"
                  strokeWidth="1"
                />
                <text
                  x={padL - 6}
                  y={g.y + 4}
                  textAnchor="end"
                  fontSize="9"
                  fill="#475569"
                >
                  {g.label}
                </text>
              </g>
            ))}

            {/* Baseline */}
            <line
              x1={padL}
              y1={padT + chartH}
              x2={W - padR}
              y2={padT + chartH}
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="1"
            />

            {/* X labels */}
            {buckets.map((b, i) => {
              if (i % labelStep !== 0) return null;
              return (
                <text
                  key={i}
                  x={xPos(i)}
                  y={padT + chartH + 14}
                  textAnchor="middle"
                  fontSize="9"
                  fill="#475569"
                >
                  {b.label}
                </text>
              );
            })}

            {/* Series lines */}
            {SERIES.map((s) => (
              <path
                key={s.key}
                d={makePath(s.key)}
                fill="none"
                stroke={s.color}
                strokeWidth="1.5"
                strokeLinecap="round"
                opacity={tooltip ? 0.3 : 1}
              />
            ))}

            {/* Hover line */}
            {tooltip && (
              <line
                x1={tooltip.svgX}
                y1={padT}
                x2={tooltip.svgX}
                y2={padT + chartH}
                stroke="rgba(255,255,255,0.2)"
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            )}

            {/* Hover dots */}
            {tooltip &&
              SERIES.map((s) => {
                const val = tooltip.bucket[s.key] || 0;
                if (!val) return null;
                return (
                  <circle
                    key={s.key}
                    cx={tooltip.svgX}
                    cy={yPos(val)}
                    r="4"
                    fill={s.color}
                    stroke="#0f172a"
                    strokeWidth="2"
                  />
                );
              })}
          </svg>

          {/* Tooltip */}
          {tooltip &&
            (() => {
              const entries = SERIES.map((s) => ({
                ...s,
                val: tooltip.bucket[s.key] || 0,
              })).filter((e) => e.val > 0);
              if (!entries.length) return null;
              const isRight = tooltip.clientX > 280;
              return (
                <div
                  className="absolute z-50 pointer-events-none"
                  style={{
                    top: "40%",
                    left: isRight
                      ? `calc(${(tooltip.svgX / W) * 100}% - 8px)`
                      : `calc(${(tooltip.svgX / W) * 100}% + 8px)`,
                    transform: isRight
                      ? "translate(-100%, -50%)"
                      : "translate(0%, -50%)",
                  }}
                >
                  <div className="bg-slate-900 border border-white/15 rounded-xl shadow-2xl px-3 py-2.5 min-w-[140px]">
                    <p className="text-xs text-slate-400 font-medium pb-1.5 mb-1.5 border-b border-white/10">
                      {tooltip.bucket.label}
                    </p>
                    <div className="space-y-1">
                      {entries.map((e) => (
                        <div
                          key={e.key}
                          className="flex items-center justify-between gap-3"
                        >
                          <div className="flex items-center gap-1.5">
                            <span
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: e.color }}
                            />
                            <span className="text-xs text-slate-400">
                              {e.label}
                            </span>
                          </div>
                          <span className="text-xs font-semibold text-white">
                            {e.val}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
        </div>
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
        {SERIES.map((s) => (
          <div key={s.key} className="flex items-center gap-1.5">
            <span
              className="inlin e-block w-3 h-0.5 rounded"
              style={{ backgroundColor: s.color }}
            />
            <span className="text-xs text-slate-400">{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Main Page ──
const EmailsPage = () => {
  const [range, setRange] = useState("7d");
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError] = useState("");

  // Initial load — fetches everything
  const fetchEmailStats = async (selectedRange) => {
    try {
      setLoading(true);
      setError("");
      const data = await getEmailsDashboard(selectedRange);
      setApiData(data);
    } catch (err) {
      setError("Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  // Range change — only refreshes the trend chart
  const handleRangeChange = async (newRange) => {
    setRange(newRange);
    try {
      setChartLoading(true);
      const data = await getEmailsDashboard(newRange);
      setApiData((prev) => ({ ...prev, delivery_trend: data.delivery_trend }));
    } catch (err) {
      // silently fail on chart reload
    } finally {
      setChartLoading(false);
    }
  };

  useEffect(() => {
    fetchEmailStats(range);
  }, []);

  if (loading) return <PageLoader />;
  if (error)
    return (
      <div className="flex items-center justify-center h-48">
        <p className="text-red-400 text-sm">{error}</p>
      </div>
    );

  // ── Map API response to KPI cards ──
  const kpis_raw = apiData?.kpis || {};
  const trend = apiData?.delivery_trend || { buckets: [] };
  const decisions = apiData?.recent_auto_resend_decisions || [];

  const kpis = [
    {
      label: "Total Emails",
      value: fmt(kpis_raw.total_emails?.value),
      sub:
        kpis_raw.total_emails?.delta_label ??
        kpis_raw.total_emails?.delta_pct ??
        null,
      subColor: "text-emerald-400",
    },
    {
      label: "Delivered",
      value: fmt(kpis_raw.delivered?.value),
      sub:
        kpis_raw.delivered?.label ??
        `${kpis_raw.delivered?.success_rate ?? ""}% success rate`,
      subColor: "text-slate-400",
    },
    {
      label: "Bounced",
      value: fmt(kpis_raw.bounced?.value),
      sub:
        kpis_raw.bounced?.label ??
        `${kpis_raw.bounced?.traffic_pct ?? ""}% of traffic`,
      subColor: "text-red-400",
    },
    {
      label: "Pending / Queueing",
      value: fmt(kpis_raw.pending?.value),
      sub: kpis_raw.pending?.label ?? null,
      subColor: "text-slate-400",
    },
    {
      label: "Auto Resent · BackUp Domain",
      value: fmt(kpis_raw.auto_resent_backup_domain?.value),
      sub: null,
      subColor: "",
    },
    {
      label: "Needs Manual Action",
      value: fmt(kpis_raw.needs_manual_action?.value),
      sub: kpis_raw.needs_manual_action?.label ?? null,
      subColor: "text-amber-400",
    },
  ];

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div>
        <h1 className="text-2xl font-semibold text-white">Emails Dashboard</h1>
        <p className="text-sm text-slate-400">
          Email delivery overview and resend decisions.
        </p>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map((kpi, i) => (
          <div
            key={i}
            className="bg-slate-900/60 border border-white/10 rounded-xl px-4 py-4"
          >
            <p className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2 leading-tight">
              {kpi.label}
            </p>
            <p className="text-2xl font-semibold text-white tabular-nums">
              {kpi.value}
            </p>
            {kpi.sub && (
              <p className={`text-xs mt-1 ${kpi.subColor}`}>{kpi.sub}</p>
            )}
          </div>
        ))}
      </div>

      {/* ── Chart + Table ── */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Delivery Trend */}
        <div className="lg:col-span-2 bg-slate-900/60 border border-white/10 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-white">Delivery Trend</h2>
          <p className="text-xs text-slate-500 mt-0.5 mb-4">
            Sent vs Delivered vs Bounced vs Auto Resent vs Needs Action
          </p>
          <DeliveryTrendChart
            trend={trend}
            range={range}
            onRangeChange={handleRangeChange}
            loading={chartLoading}
          />
        </div>

        {/* Last Auto Resend Decisions */}
        <div className="lg:col-span-2 bg-slate-900/60 border border-white/10 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-white/10">
            <h2 className="text-sm font-semibold text-white">
              Last Auto Resend Decisions
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table
              className="w-full border-collapse"
              style={{ minWidth: "460px" }}
            >
              <thead>
                <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-400">
                  <th className="px-4 py-3 text-center whitespace-nowrap">
                    Order ID
                  </th>
                  <th className="px-4 py-3 text-center whitespace-nowrap">
                    Type
                  </th>
                  <th className="px-4 py-3 text-center whitespace-nowrap">
                    1st
                  </th>
                  <th className="px-4 py-3 text-center whitespace-nowrap">
                    2nd
                  </th>
                  <th className="px-4 py-3 text-center whitespace-nowrap">
                    Email
                  </th>
                </tr>
              </thead>
              <tbody>
                {decisions.map((d, i) => (
                  <tr
                    key={i}
                    className="border-b text-center border-white/5 last:border-none hover:bg-slate-800/40 transition"
                  >
                    <td className="px-4 py-3 text-xs font-mono text-slate-300 whitespace-nowrap">
                      {d.order_id || "-"}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-1.5 py-0.5 rounded text-xs bg-slate-800 border border-white/10 text-slate-300 whitespace-nowrap">
                        {d.email_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {attemptBadge(d.first_attempt)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {attemptBadge(d.second_attempt)}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">
                      <div className="break-all">{d.recipient}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <EmailsLogsSection />          

    </div>
  );
};

export default EmailsPage;
